import type { PlayerProjection } from "@saboteur/contracts";
import type { ConnectionStatus } from "@client/realtime/game-socket";
import { RulesSheet } from "@client/components/rules-sheet";
import { TurnNotification } from "@client/components/turn-notification";
import { PlayerStatus, type PlayerToolAction } from "../board/player-strip";
import { RoleDetails } from "./role-details";
import "./game-header.css";

type Participant = PlayerProjection["public"]["participants"][number];
export interface GameHeaderProps {
  participant: Participant;
  activeParticipant: Participant | undefined;
  connectionStatus: ConnectionStatus;
  toolAction: PlayerToolAction | undefined;
  role: PlayerProjection["self"]["role"];
  roleAssignmentKey: string;
  roleReminderKey: string;
}

function HeaderOrnaments() {
  return (
    <svg
      className="game-header__frame"
      viewBox="0 0 1000 140"
      preserveAspectRatio="none"
      fill="none"
      aria-hidden="true"
    >
      <path
        className="game-header__frame-edge"
        d="M16 12H435Q500-8 565 12H984Q998 12 998 26V124Q998 138 984 138H16Q2 138 2 124V26Q2 12 16 12Z"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d="M16 12H435M565 12H984M12 20l10 12m-10 0 10-12M978 20l10 12m-10 0 10-12M12 118l10 12m-10 0 10-12M978 118l10 12m-10 0 10-12"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function HeaderWordmark() {
  return (
    <a className="game-header__wordmark" href="/" aria-label="Saboteur home">
      <svg className="game-header__mountain" viewBox="0 0 100 44" fill="none" aria-hidden="true">
        <path
          d="m3 40 23-22 7 6L50 3l12 18 8-9 27 29-27-21-8 11-12-18-16 22-8-10Z"
          fill="currentColor"
        />
        <path d="m28 35 9-5 13-17-1 18 8-6 14 14" stroke="currentColor" strokeWidth="2" />
      </svg>
      <span>Saboteur</span>
      <svg className="game-header__pickaxes" viewBox="0 0 240 34" fill="none" aria-hidden="true">
        <path d="M2 17h76l8-2 6 2-6 2-8-2m160 0h-76l-8-2-6 2 6 2 8-2" stroke="currentColor" />
        <g stroke="currentColor" strokeLinecap="round">
          <path d="m108 29 24-24m-23 3 24 22" strokeWidth="3" />
          <path d="M123 4q10-2 15 8m-35 1q4-11 15-9" strokeWidth="3" />
        </g>
      </svg>
    </a>
  );
}

export function GameHeader({
  participant,
  activeParticipant,
  connectionStatus,
  toolAction,
  role,
  roleAssignmentKey,
  roleReminderKey,
}: GameHeaderProps) {
  const activeSelf = activeParticipant?.participantId === participant.participantId;
  let subject = "Waiting…";
  let detail = "";
  let active = false;
  if (connectionStatus !== "online") {
    subject = connectionStatus === "connecting" ? "Connecting…" : "Offline";
  } else if (activeParticipant && !activeParticipant.connected) {
    subject = activeParticipant.displayName;
    detail = " offline";
  } else if (activeSelf) {
    subject = "Your";
    detail = " turn!";
    active = true;
  } else if (activeParticipant) {
    subject = `${activeParticipant.displayName}’s`;
    detail = " turn";
  }
  return (
    <header className="game-topbar game-header">
      <HeaderOrnaments />
      <div
        className={`self-player-status${activeSelf ? " is-active" : ""}`}
        data-self="true"
        aria-current={activeSelf ? "true" : undefined}
        aria-label="Your player status"
      >
        <PlayerStatus participant={participant} self toolAction={toolAction} />
      </div>
      <HeaderWordmark />
      <div className="game-header__right">
        <TurnNotification active={active} subject={subject} detail={detail} />
        <div className="topbar-actions">
          <RulesSheet compact />
          <RoleDetails role={role} resetKey={roleAssignmentKey} reminderKey={roleReminderKey} />
        </div>
      </div>
    </header>
  );
}
